import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

interface Signal {
  signalType: string;
  detail: string;
  severity: string;
}

interface WatchlistEntry {
  loanId: number;
  // Silo discriminator (PRIVATE_CREDIT / LEVERAGED_LOAN / ...). REQUIRED on promote:
  // loan ids collide across silos (PC #23 = Meridian vs LL #23 = Informatica), so
  // without this the backend defaults to LEVERAGED_LOAN and opens a case on the wrong loan.
  source: string;
  securityId: number | null;
  portfolioId: number | null;
  borrowerName: string;
  loanIdentifier: string;
  sector: string | null;
  currentExposure: number;
  spRating: string | null;
  moodysRating: string | null;
  riskScore: number;
  severity: string;
  recommendedSituationType: string;
  signals: Signal[];
}

interface PendingPromotion {
  id: number;
  loanSource: string;
  loanId: number;
  situationType: string;
  rationale: string;
  evidenceRef: string;
  signalSnapshot: string;
  requestedBy: string;
  requestedAt: string;
}

interface Props {
  embedded?: boolean;
}

// ---------- styled ----------

const Container = styled.div<{ $embedded?: boolean }>`
  max-width: 1800px;
  margin: 0 auto;
  padding: ${p => p.$embedded ? '1rem' : '2rem'};
  color: #e2e8f0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Title = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const Subtitle = styled.div`
  color: #94a3b8;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div<{ $color?: string }>`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-left: 4px solid ${p => p.$color || '#f59e0b'};
  border-radius: 12px;
  padding: 1.25rem;
`;

const StatLabel = styled.div`
  color: #94a3b8;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
`;

const StatValue = styled.div`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
`;

const TableWrap = styled.div`
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  overflow: hidden;
`;

const ApprovalQueue = styled.div`
  margin: 0 0 1.5rem;
  padding: 1rem;
  border: 1px solid rgba(245, 158, 11, 0.35);
  border-radius: 12px;
  background: rgba(245, 158, 11, 0.06);
`;

const ApprovalTitle = styled.div`
  color: #fbbf24;
  font-weight: 700;
  margin-bottom: 0.75rem;
`;

const ApprovalRow = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 0.7fr) minmax(240px, 2fr) minmax(180px, 1fr) auto;
  gap: 1rem;
  align-items: center;
  padding: 0.75rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 0.8rem;

  &:first-of-type {
    border-top: none;
  }
`;

const ApproveBtn = styled.button`
  background: #0f766e;
  color: white;
  border: 1px solid #14b8a6;
  border-radius: 7px;
  padding: 0.55rem 0.8rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: wait;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.875rem 1rem;
  background: rgba(30, 41, 59, 0.7);
  color: #94a3b8;
  font-weight: 600;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  vertical-align: top;
`;

const Tr = styled.tr`
  &:hover {
    background: rgba(30, 41, 59, 0.3);
  }
`;

const RankBadge = styled.div<{ $severity: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.875rem;
  background: ${p => p.$severity === 'CRITICAL' ? 'rgba(239, 68, 68, 0.15)'
                  : p.$severity === 'HIGH' ? 'rgba(245, 158, 11, 0.15)'
                  : 'rgba(234, 179, 8, 0.15)'};
  color: ${p => p.$severity === 'CRITICAL' ? '#ef4444'
              : p.$severity === 'HIGH' ? '#f59e0b'
              : '#eab308'};
  border: 1px solid ${p => p.$severity === 'CRITICAL' ? 'rgba(239, 68, 68, 0.3)'
                         : p.$severity === 'HIGH' ? 'rgba(245, 158, 11, 0.3)'
                         : 'rgba(234, 179, 8, 0.3)'};
`;

const ScoreBar = styled.div<{ $score: number; $severity: string }>`
  width: 100%;
  height: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 5px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${p => p.$score}%;
    background: ${p => p.$severity === 'CRITICAL' ? 'linear-gradient(90deg, #dc2626, #ef4444)'
                    : p.$severity === 'HIGH' ? 'linear-gradient(90deg, #d97706, #f59e0b)'
                    : 'linear-gradient(90deg, #ca8a04, #eab308)'};
  }
`;

const ScoreText = styled.div<{ $severity: string }>`
  font-weight: 700;
  margin-top: 4px;
  color: ${p => p.$severity === 'CRITICAL' ? '#ef4444'
              : p.$severity === 'HIGH' ? '#f59e0b'
              : '#eab308'};
`;

const Borrower = styled.div`
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
`;

const BorrowerMeta = styled.div`
  color: #64748b;
  font-size: 0.7rem;
  margin-top: 0.25rem;
`;

const SignalChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const Chip = styled.span<{ $type: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 5px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: ${p =>
      p.$type === 'COVENANT_BREACH' ? 'rgba(239, 68, 68, 0.12)'
    : p.$type === 'DISTRESSED_PRICE' ? 'rgba(245, 158, 11, 0.12)'
    : p.$type === 'DISTRESSED_RATING' ? 'rgba(168, 85, 247, 0.12)'
    : p.$type === 'DEFAULT' ? 'rgba(220, 38, 38, 0.18)'
    : 'rgba(148, 163, 184, 0.1)'};
  color: ${p =>
      p.$type === 'COVENANT_BREACH' ? '#ef4444'
    : p.$type === 'DISTRESSED_PRICE' ? '#f59e0b'
    : p.$type === 'DISTRESSED_RATING' ? '#a855f7'
    : p.$type === 'DEFAULT' ? '#dc2626'
    : '#94a3b8'};
  border: 1px solid currentColor;
`;

const ChipDetail = styled.div`
  color: #94a3b8;
  font-size: 0.75rem;
  margin-top: 0.4rem;
  line-height: 1.4;
`;

const PromoteBtn = styled.button<{ $busy?: boolean }>`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  padding: 0.6rem 1rem;
  border-radius: 7px;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: ${p => p.$busy ? 'wait' : 'pointer'};
  opacity: ${p => p.$busy ? 0.6 : 1};
  white-space: nowrap;
  transition: all 0.15s;

  &:hover {
    transform: ${p => p.$busy ? 'none' : 'translateY(-1px)'};
    box-shadow: ${p => p.$busy ? 'none' : '0 4px 12px rgba(239, 68, 68, 0.4)'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #64748b;
`;

const ErrorBox = styled.div`
  padding: 1rem;
  margin-bottom: 1rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #ef4444;
`;

// ---------- helpers ----------

const fmtCurrency = (v: number) => {
  if (v == null) return '$0';
  if (v >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(2)}B`;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  return `$${v.toLocaleString()}`;
};

const chipLabel = (type: string) => {
  switch (type) {
    case 'COVENANT_BREACH': return 'Covenant Breach';
    case 'DISTRESSED_PRICE': return 'Distressed Price';
    case 'DISTRESSED_RATING': return 'Low Rating';
    case 'DEFAULT': return 'Default';
    default: return type;
  }
};

// ---------- component ----------

const CreditWatchlistScreen: React.FC<Props> = ({ embedded = false }) => {
  const [rows, setRows] = useState<WatchlistEntry[]>([]);
  const [pending, setPending] = useState<PendingPromotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyLoanId, setBusyLoanId] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [promoteModal, setPromoteModal] = useState<WatchlistEntry | null>(null);
  const [promoteRationale, setPromoteRationale] = useState('');
  const [promoteEvidence, setPromoteEvidence] = useState('');
  const [promoteError, setPromoteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [watchlistResponse, pendingResponse] = await Promise.all([
        axios.get('/api/credit/watchlist'),
        axios.get('/api/credit/watchlist/promotions/pending'),
      ]);
      setRows(Array.isArray(watchlistResponse.data) ? watchlistResponse.data : []);
      setPending(Array.isArray(pendingResponse.data) ? pendingResponse.data : []);
    } catch (e: any) {
      console.error('Failed to load watchlist', e);
      setError(e?.response?.data?.error || 'Failed to load watchlist');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const promote = (row: WatchlistEntry) => {
    setPromoteError(null);
    setPromoteRationale(row.signals[0]?.detail || '');
    setPromoteEvidence('');
    setPromoteModal(row);
  };

  const submitPromotion = async () => {
    if (!promoteModal) return;
    if (!promoteRationale.trim()) { setPromoteError('A promotion rationale is required.'); return; }
    if (!promoteEvidence.trim()) {
      setPromoteError('An evidence reference is required (certificate, valuation, agent notice, or approved credit memo).');
      return;
    }
    const row = promoteModal;
    try {
      setBusyLoanId(row.loanId);
      await axios.post('/api/credit/watchlist/promote', {
        source: row.source,   // disambiguate the silo — loan ids collide across silos
        loanId: row.loanId,
        situationType: row.recommendedSituationType,
        rationale: promoteRationale.trim(),
        evidenceRef: promoteEvidence.trim(),
        signalSnapshot: {
          riskScore: row.riskScore,
          severity: row.severity,
          currentExposure: row.currentExposure,
          signals: row.signals,
        },
      });
      setPromoteModal(null);
      await load();
      setBusyLoanId(null);
    } catch (e: any) {
      setPromoteError(e?.response?.data?.error || 'Failed to submit promotion');
      setBusyLoanId(null);
    }
  };

  const approvePromotion = async (promotion: PendingPromotion) => {
    try {
      setBusyLoanId(promotion.loanId);
      const { data } = await axios.post(
        `/api/credit/watchlist/promotions/${promotion.id}/approve`,
      );
      const p = new URLSearchParams(searchParams);
      p.set('sub', 'distressed-detail');
      p.set('id', String(data.id));
      setSearchParams(p, { replace: false });
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to approve promotion');
      setBusyLoanId(null);
    }
  };

  const critical = rows.filter(r => r.severity === 'CRITICAL').length;
  const high = rows.filter(r => r.severity === 'HIGH').length;
  const totalExposure = rows.reduce((sum, r) => sum + (r.currentExposure || 0), 0);

  return (
    <Container $embedded={embedded}>
      <Header>
        <div>
          <Title>Credit Watchlist</Title>
          <Subtitle>
            Loans showing deterioration signals — covenant breach, distressed pricing,
            rating downgrade, default. Submit a promotion for independent approval.
          </Subtitle>
        </div>
      </Header>

      {error && <ErrorBox>{error}</ErrorBox>}

      {pending.length > 0 && (
        <ApprovalQueue>
          <ApprovalTitle>Pending distressed-situation approvals ({pending.length})</ApprovalTitle>
          {pending.map(proposal => (
            <ApprovalRow key={proposal.id}>
              <div>
                <strong>Loan #{proposal.loanId}</strong>
                <div style={{ color: '#94a3b8' }}>{proposal.loanSource}</div>
              </div>
              <div>
                <div>{proposal.rationale}</div>
                <div style={{ color: '#94a3b8' }}>Evidence: {proposal.evidenceRef}</div>
              </div>
              <div style={{ color: '#94a3b8' }}>
                Requested by {proposal.requestedBy || 'unknown'}
              </div>
              <ApproveBtn
                disabled={busyLoanId === proposal.loanId}
                onClick={() => approvePromotion(proposal)}
              >
                {busyLoanId === proposal.loanId ? 'Approving…' : 'Approve'}
              </ApproveBtn>
            </ApprovalRow>
          ))}
        </ApprovalQueue>
      )}

      <StatsRow>
        <StatCard $color="#ef4444">
          <StatLabel>Critical</StatLabel>
          <StatValue>{critical}</StatValue>
        </StatCard>
        <StatCard $color="#f59e0b">
          <StatLabel>High</StatLabel>
          <StatValue>{high}</StatValue>
        </StatCard>
        <StatCard $color="#eab308">
          <StatLabel>Total Watching</StatLabel>
          <StatValue>{rows.length}</StatValue>
        </StatCard>
        <StatCard $color="#3b82f6">
          <StatLabel>Aggregate Exposure</StatLabel>
          <StatValue>{fmtCurrency(totalExposure)}</StatValue>
        </StatCard>
      </StatsRow>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th style={{ width: 70 }}>Rank</Th>
              <Th>Borrower</Th>
              <Th style={{ width: 160 }}>Exposure</Th>
              <Th style={{ width: 100 }}>Rating</Th>
              <Th style={{ width: 220 }}>Risk Score</Th>
              <Th>Signals</Th>
              <Th style={{ width: 160 }}>Action</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><Td colSpan={7}><EmptyState>Loading…</EmptyState></Td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><Td colSpan={7}>
                <EmptyState>
                  No deteriorating loans detected. All clear for now.
                </EmptyState>
              </Td></tr>
            )}
            {rows.map((row, idx) => (
              <Tr key={`${row.source}-${row.loanId}`}>
                <Td>
                  <RankBadge $severity={row.severity}>{idx + 1}</RankBadge>
                </Td>
                <Td>
                  <Borrower>{row.borrowerName}</Borrower>
                  <BorrowerMeta>
                    {row.loanIdentifier}
                    {row.sector ? ` • ${row.sector}` : ''}
                  </BorrowerMeta>
                </Td>
                <Td>
                  <div style={{ color: 'white', fontWeight: 600 }}>
                    {fmtCurrency(row.currentExposure)}
                  </div>
                </Td>
                <Td>
                  <div style={{ color: '#cbd5e1', fontWeight: 600 }}>
                    {row.spRating || '—'}
                  </div>
                  <div style={{ color: '#64748b', fontSize: '0.7rem' }}>
                    {row.moodysRating || ''}
                  </div>
                </Td>
                <Td>
                  <ScoreBar $score={row.riskScore} $severity={row.severity} />
                  <ScoreText $severity={row.severity}>
                    {row.riskScore} / 100 — {row.severity}
                  </ScoreText>
                </Td>
                <Td>
                  <SignalChips>
                    {row.signals.map((s, i) => (
                      <Chip key={i} $type={s.signalType}>{chipLabel(s.signalType)}</Chip>
                    ))}
                  </SignalChips>
                  {row.signals.length > 0 && (
                    <ChipDetail>{row.signals[0].detail}</ChipDetail>
                  )}
                </Td>
                <Td>
                  <PromoteBtn
                    data-testid="watchlist-promote"
                    data-loan-id={row.loanId}
                    $busy={busyLoanId === row.loanId}
                    disabled={busyLoanId === row.loanId}
                    onClick={() => promote(row)}
                  >
                    {busyLoanId === row.loanId ? 'Submitting…' : 'Submit for Distressed Review'}
                  </PromoteBtn>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>

      {promoteModal && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' }}
          onClick={() => setPromoteModal(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 520, background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '1.5rem', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}
          >
            <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#f59e0b', fontWeight: 700, marginBottom: 4 }}>
              Distressed Review Submission
            </div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: 'white', marginBottom: 4 }}>{promoteModal.borrowerName}</div>
            <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '1rem' }}>
              Opens a MONITORING distressed situation ({promoteModal.recommendedSituationType}) pending independent approval.
              This does not impair the position.
            </div>
            <label style={{ display: 'block', fontSize: '0.7rem', color: '#94a3b8', marginBottom: 4 }}>Promotion rationale *</label>
            <textarea
              value={promoteRationale}
              onChange={(e) => { setPromoteRationale(e.target.value); setPromoteError(null); }}
              rows={3}
              style={{ width: '100%', background: '#020617', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '0.5rem 0.75rem', color: 'white', fontSize: '0.8rem', resize: 'vertical', marginBottom: '0.75rem' }}
            />
            <label style={{ display: 'block', fontSize: '0.7rem', color: '#94a3b8', marginBottom: 4 }}>
              Evidence reference * — certificate, valuation, agent notice, or approved credit memo
            </label>
            <input
              value={promoteEvidence}
              onChange={(e) => { setPromoteEvidence(e.target.value); setPromoteError(null); }}
              placeholder="e.g., Q2 compliance certificate #CC-2026-114"
              style={{ width: '100%', background: '#020617', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '0.5rem 0.75rem', color: 'white', fontSize: '0.8rem', marginBottom: '0.5rem' }}
            />
            {promoteError && <div style={{ color: '#f87171', fontSize: '0.75rem', marginBottom: '0.5rem' }}>{promoteError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '0.75rem' }}>
              <button
                onClick={() => setPromoteModal(null)}
                style={{ padding: '0.45rem 0.9rem', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: 'transparent', color: '#cbd5e1', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={submitPromotion}
                disabled={busyLoanId === promoteModal.loanId}
                style={{ padding: '0.45rem 0.9rem', borderRadius: 8, border: '1px solid rgba(245,158,11,0.5)', background: 'rgba(245,158,11,0.15)', color: '#fbbf24', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', opacity: busyLoanId === promoteModal.loanId ? 0.6 : 1 }}
              >
                {busyLoanId === promoteModal.loanId ? 'Submitting…' : 'Submit for Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default CreditWatchlistScreen;
